import { useEffect, useState, useCallback } from "react";
import { Wand2, Palette } from "lucide-react";
import { toast } from "sonner";
import {
  adminListReadyMade, adminCreateReadyMade, adminUpdateReadyMade, adminDeleteReadyMade,
  adminMigrateColours, adminNormalizeReadyMadeColours,
  adminReadyMadeBulkUpdate, adminReadyMadeBulkDelete, adminDuplicateReadyMade,
  adminReadyMadeReorder,
} from "../../lib/api";
import AdminListingManager from "./AdminListingManager";
import ReadyMadeListingEditor from "./ReadyMadeListingEditor";

export function ReadyMadeTab({ pw }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [normalizing, setNormalizing] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    adminListReadyMade(pw).then((data) => { setItems(data); setLoading(false); });
  }, [pw]);
  useEffect(reload, [reload]);

  const handleSave = async (data) => {
    try {
      if (editing) await adminUpdateReadyMade(editing.id, data, pw);
      else await adminCreateReadyMade(data, pw);
      reload();
      setEditing(null);
      setCreating(false);
      toast.success("Listing saved");
    } catch {
      toast.error("Save failed");
    }
  };

  const cleanColours = async () => {
    if (!window.confirm("Convert all hex codes in colour swatches to real names? This is safe to run repeatedly.")) return;
    setCleaning(true);
    try {
      const res = await adminMigrateColours(pw);
      toast.success(`Cleaned ${res.hex_values_rewritten} hex values across ${res.listings_updated} listings`);
      reload();
    } catch (e) {
      toast.error("Clean failed: " + (e?.message || "unknown"));
    } finally {
      setCleaning(false);
    }
  };

  const normalizeColours = async () => {
    if (!window.confirm(
      "Restrict every ready-made design to Black / White / Grey (and any Dragon Ball / DBZ product to Orange)?\n\n"
      + "This is safe to run repeatedly — a second run does nothing.",
    )) return;
    setNormalizing(true);
    try {
      const res = await adminNormalizeReadyMadeColours(pw);
      const parts = [
        `${res.updated} product${res.updated === 1 ? "" : "s"} updated`,
        res.unchanged ? `${res.unchanged} unchanged` : null,
        res.dbz_updated ? `${res.dbz_updated} narrowed to Orange (DBZ)` : null,
      ].filter(Boolean);
      toast.success(`Colours normalized — ${parts.join(" · ")}`, { duration: 10000 });
      reload();
    } catch (e) {
      toast.error("Normalize failed: " + (e?.response?.data?.detail || e?.message || "unknown"));
    } finally {
      setNormalizing(false);
    }
  };

  return (
    <>
      <div className="flex items-center justify-end gap-2 mb-3">
        <button
          data-testid="normalize-colours-btn"
          onClick={normalizeColours}
          disabled={normalizing}
          title="Collapse every ready-made design to Black/White/Grey — and any Dragon Ball / DBZ product to Orange."
          className="border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] px-4 py-2 font-mono text-[10px] tracking-widest uppercase flex items-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Palette size={13} /> {normalizing ? "Normalizing…" : "Normalize Colours"}
        </button>
        <button
          data-testid="clean-colours-btn"
          onClick={cleanColours}
          disabled={cleaning}
          title="Rewrites hex codes in colour swatches (#000000 → Black) across every listing"
          className="border border-[#222] hover:border-[var(--tc-red)] hover:text-[var(--tc-red)] px-4 py-2 font-mono text-[10px] tracking-widest uppercase flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          <Wand2 size={13} /> {cleaning ? "Cleaning…" : "Clean Colour Codes"}
        </button>
      </div>
      <AdminListingManager
        items={items}
        loading={loading}
        mode="ready-made"
        storageKey="tc_admin_readymade"
        onReload={reload}
        onEdit={setEditing}
        onNew={() => setCreating(true)}
        updateItem={(id, patch) => adminUpdateReadyMade(id, patch, pw)}
        deleteItem={(id) => adminDeleteReadyMade(id, pw)}
        duplicateItem={(id) => adminDuplicateReadyMade(id, pw)}
        bulkUpdate={(payload) => adminReadyMadeBulkUpdate(payload, pw)}
        bulkDelete={(payload) => adminReadyMadeBulkDelete(payload, pw)}
        reorderItems={(ids) => adminReadyMadeReorder(ids, pw)}
        thumbFor={(p) => p.image_front}
        priceOf={(p) => Number(p.price ?? 0)}
        viewUrl={(p) => `/shop/${p.slug}`}
      />
      {(editing || creating) && (
        <ReadyMadeListingEditor
          item={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSave={handleSave}
        />
      )}
    </>
  );
}

export default ReadyMadeTab;
